import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  LogOut,
  Save,
  Send,
  User,
  Phone,
  CheckCircle,
  Trash2,
  Info,
  AlertTriangle,
  Users,
  Copy,
  Mail,
  Eye,
  Globe,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

import PDFPreview from "../components/PDFPreview";
import VersionCompare from "../components/VersionCompare";
import LanguageSelector from "../components/LanguageSelector";
import i18n from "../i18n";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_telenot-portal/artifacts/5ccwvbhz_PLU_Logo_Claim_RGB.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_CODES = [
  { code: "+49", country: "Deutschland", flag: "🇩🇪" },
  { code: "+43", country: "Österreich", flag: "🇦🇹" },
  { code: "+41", country: "Schweiz", flag: "🇨🇭" },
  { code: "+31", country: "Niederlande", flag: "🇳🇱" },
  { code: "+32", country: "Belgien", flag: "🇧🇪" },
  { code: "+33", country: "Frankreich", flag: "🇫🇷" },
  { code: "+39", country: "Italien", flag: "🇮🇹" },
  { code: "+44", country: "Großbritannien", flag: "🇬🇧" },
  { code: "+45", country: "Dänemark", flag: "🇩🇰" },
  { code: "+46", country: "Schweden", flag: "🇸🇪" },
  { code: "+47", country: "Norwegen", flag: "🇳🇴" },
  { code: "+48", country: "Polen", flag: "🇵🇱" },
  { code: "+420", country: "Tschechien", flag: "🇨🇿" },
  { code: "+352", country: "Luxemburg", flag: "🇱🇺" },
];

const CustomerForm = () => {
  const { t, i18n: i18nInstance } = useTranslation();
  const { user, logout, api } = useAuth();
  const signatureRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectStatus, setProjectStatus] = useState("pending");
  const [validationErrors, setValidationErrors] = useState([]);
  const [teamToken, setTeamToken] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [isSharingTeam, setIsSharingTeam] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [showVersionCompare, setShowVersionCompare] = useState(false);
  const [previousVersion, setPreviousVersion] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(1);
  const [languageLoaded, setLanguageLoaded] = useState(false);
  const [projectLanguage, setProjectLanguage] = useState("de");
  const [customMessageTypes, setCustomMessageTypes] = useState([]);
  
  // Participant types - dynamically translated
  const PARTICIPANT_TYPES = [
    { key: "smsMobile", label: t("participantTypes.smsMobile", "SMS via mobile") },
    { key: "voiceMobile", label: t("participantTypes.voiceMobile", "Voice via mobile") },
    { key: "voiceLandline", label: t("participantTypes.voiceLandline", "Voice via landline") },
  ];
  
  // Message types - use custom project types or fallback to defaults
  const MESSAGE_TYPES = customMessageTypes.length > 0 
    ? customMessageTypes.map(mt => ({
        key: mt.key,
        label: projectLanguage.startsWith("en") ? mt.label_en : mt.label_de,
        required: mt.required
      }))
    : [
        { key: "fault1", label: t("messageTypes.fault1", "Fault Priority 1"), required: true },
        { key: "fault2", label: t("messageTypes.fault2", "Fault Priority 2"), required: true },
        { key: "personAlarm", label: t("messageTypes.personAlarm", "Person alarm"), required: true },
        { key: "deviceFault", label: t("messageTypes.deviceFault", "Device fault"), required: true },
        { key: "testMessage", label: t("messageTypes.testMessage", "Test message"), required: true },
      ];
  
  const [formData, setFormData] = useState({
    project_name: "",
    customer_name: "",
    date: new Date().toLocaleDateString("de-DE"),
    participants: Array.from({ length: 10 }, (_, i) => ({
      number: i + 1,
      type: "",
      name: "",
      country_code: "+49",
      phone: "",
    })),
    sequences: Array.from({ length: 8 }, (_, i) => ({
      line: i + 1,
      message_type: "",
      message_text: "",
      assignments: [],
      cycle_count: 12,
      cycle_time: 30,
    })),
    confirmation: false,
    signature: null,
  });

  // Set language from project on load
  useEffect(() => {
    const setProjectLanguageFromProject = async () => {
      if (user?.project_id) {
        try {
          // Get project info to set language
          const response = await api.get(`/projects/${user.project_id}`);
          const projectLang = response.data.language || "de";
          setProjectLanguage(projectLang);
          
          // Load custom message types from project if available
          if (response.data.message_types && response.data.message_types.length > 0) {
            setCustomMessageTypes(response.data.message_types);
          }
          
          await i18n.changeLanguage(projectLang);
          if (projectLang === "ar") {
            document.documentElement.dir = "rtl";
          } else {
            document.documentElement.dir = "ltr";
          }
          setLanguageLoaded(true);
        } catch (error) {
          console.error("Failed to get project language");
          setLanguageLoaded(true);
        }
      }
    };
    setProjectLanguageFromProject();
  }, [user, api]);

  useEffect(() => {
    if (user?.project_id && languageLoaded) {
      fetchFormData();
    }
  }, [user, languageLoaded]);

  const fetchFormData = async () => {
    try {
      const response = await api.get(`/projects/${user.project_id}/form`);
      const data = response.data;
      
      // Load custom message types from form data if available
      if (data.message_types && data.message_types.length > 0) {
        setCustomMessageTypes(data.message_types);
      }
      
      // Ensure participants have country_code
      const participants = (data.participants || formData.participants).map((p) => ({
        ...p,
        country_code: p.country_code || "+49",
      }));
      
      // Ensure sequences have default cycle values
      const sequences = (data.sequences || formData.sequences).map((s) => ({
        ...s,
        cycle_count: s.cycle_count ?? 12,
        cycle_time: s.cycle_time ?? 30,
      }));
      
      setFormData({
        project_name: data.project_name || "",
        customer_name: data.customer_name || "",
        date: data.date || new Date().toLocaleDateString("de-DE"),
        participants,
        sequences,
        confirmation: data.confirmation || false,
        signature: data.signature || null,
      });
      setProjectStatus(data.project_status || "pending");
      setTeamToken(data.team_token || null);
      setCurrentVersion(data.version || 1);
      
      // Check for previous version
      if (data.version && data.version > 1) {
        try {
          const prevRes = await api.get(`/projects/${user.project_id}/previous-version`);
          if (prevRes.data.has_previous) {
            setPreviousVersion(prevRes.data);
          }
        } catch (e) {
          // Ignore - previous version not available
        }
      }
    } catch (error) {
      toast.error("Fehler beim Laden des Formulars");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/projects/${user.project_id}/form`, formData);
      toast.success("Formular gespeichert");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Speichern";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const validateForm = () => {
    const errors = [];
    const usedMessageTypes = new Set();
    
    // Only validate sequences that have a message type selected
    formData.sequences.forEach((seq) => {
      if (seq.message_type) {
        usedMessageTypes.add(seq.message_type);
        
        const validAssignments = (seq.assignments || []).filter(
          (a) => a.value && a.value !== "keine Teilnehmer" && a.value !== "no participants"
        );
        
        if (validAssignments.length === 0) {
          errors.push(t("validation.assignmentRequired", `Line ${seq.line}: At least one participant assignment required`));
        } else {
          const lastAssignment = validAssignments[validAssignments.length - 1].value;
          if (lastAssignment !== "einen Teilnehmer" && lastAssignment !== "alle Teilnehmer" && 
              lastAssignment !== "one participant" && lastAssignment !== "all participants") {
            errors.push(t("validation.mustEndWithAck", `Line ${seq.line}: Must end with "Acknowledgement by"`));
          }
        }
      }
    });
    
    // Check that all REQUIRED message types are used (only if they are marked as required)
    const requiredMessageTypes = MESSAGE_TYPES.filter(mt => mt.required);
    const requiredLabels = requiredMessageTypes.map(mt => mt.label);
    const missingTypes = requiredLabels.filter((label) => !usedMessageTypes.has(label));
    if (missingTypes.length > 0) {
      errors.push(`${t("validation.missingTypes", "Missing message types")}: ${missingTypes.join(", ")}`);
    }
    
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handlePreviewPdf = async () => {
    // Validate form first
    if (!validateForm()) {
      toast.error(t("validation.pleaseCorrect", "Please correct the errors"));
      return;
    }
    
    if (!formData.confirmation) {
      toast.error("Bitte bestätigen Sie die Richtigkeit der Angaben");
      return;
    }

    const signatureData = signatureRef.current?.toDataURL();
    if (!signatureData || signatureRef.current?.isEmpty()) {
      toast.error("Bitte unterschreiben Sie das Formular");
      return;
    }

    setIsGeneratingPdf(true);
    setShowPdfPreview(true);
    
    try {
      // Save form data first to ensure preview matches final PDF
      await handleSave();
      
      // Generate preview PDF
      const response = await api.get(`/projects/${user.project_id}/pdf/preview`, {
        responseType: 'blob'
      });
      
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfPreviewUrl(pdfUrl);
    } catch (error) {
      toast.error("Fehler beim Generieren der Vorschau");
      setShowPdfPreview(false);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setShowPdfPreview(false);
    await handleSubmit();
  };

  const handleSubmit = async () => {
    // Validate form
    if (!validateForm()) {
      toast.error("Bitte korrigieren Sie die markierten Fehler");
      return;
    }
    
    if (!formData.confirmation) {
      toast.error("Bitte bestätigen Sie die Richtigkeit der Angaben");
      return;
    }

    const signatureData = signatureRef.current?.toDataURL();
    if (!signatureData || signatureRef.current?.isEmpty()) {
      toast.error("Bitte unterschreiben Sie das Formular");
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        ...formData,
        signature: signatureData,
      };
      
      await api.post(`/projects/${user.project_id}/submit`, {
        form_data: submitData,
      });
      
      toast.success("Formular erfolgreich abgeschlossen! Sie erhalten in Kürze eine E-Mail mit dem PDF.");
      setProjectStatus("submitted");
      setFormData((prev) => ({ ...prev, signature: signatureData }));
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Absenden";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateParticipant = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      ),
    }));
  };

  const formatPhoneNumber = (value) => {
    // Only allow digits and limit length
    return value.replace(/[^\d]/g, "").slice(0, 15);
  };

  const updateSequence = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      sequences: prev.sequences.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      ),
    }));
  };

  const updateSequenceAssignment = (sequenceIndex, assignmentIndex, value) => {
    setFormData((prev) => ({
      ...prev,
      sequences: prev.sequences.map((s, i) => {
        if (i !== sequenceIndex) return s;
        
        const newAssignments = [...(s.assignments || [])];
        while (newAssignments.length <= assignmentIndex) {
          newAssignments.push({ order: newAssignments.length + 1, value: "" });
        }
        newAssignments[assignmentIndex] = { order: assignmentIndex + 1, value };
        
        return { ...s, assignments: newAssignments };
      }),
    }));
  };

  const getAvailableParticipants = () => {
    return formData.participants.filter((p) => p.name && p.phone);
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const getTeamLink = () => {
    if (!teamToken) return "";
    return `${window.location.origin}/team/${teamToken}`;
  };

  const copyTeamLink = () => {
    const link = getTeamLink();
    navigator.clipboard.writeText(link);
    toast.success("Link in die Zwischenablage kopiert");
  };

  const handleShareTeam = async () => {
    if (!shareEmail) {
      toast.error("Bitte geben Sie eine E-Mail-Adresse ein");
      return;
    }
    
    setIsSharingTeam(true);
    try {
      await api.post(`/projects/${user.project_id}/share-team`, null, {
        params: { email: shareEmail }
      });
      toast.success("Team-Einladung wurde versendet");
      setShareDialogOpen(false);
      setShareEmail("");
    } catch (error) {
      const message = error.response?.data?.detail || "Fehler beim Teilen";
      toast.error(message);
    } finally {
      setIsSharingTeam(false);
    }
  };

  const isSubmitted = projectStatus === "submitted";

  if (isLoading || !languageLoaded) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="plutec" className="h-8" />
              <span className="font-manrope font-bold text-lg text-[#1e3a5f]">
                comXline
              </span>
            </div>
            <div className="flex items-center gap-4">
              {!isSubmitted && teamToken && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShareDialogOpen(true)}
                  className="text-slate-600"
                  data-testid="share-team-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  {t("form.teamShare", "Share with team")}
                </Button>
              )}
              <LanguageSelector className="hidden sm:flex" />
              <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-slate-600 hover:text-slate-900"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("common.logout", "Logout")}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Team Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope">{t("form.teamShare", "Share with team")}</DialogTitle>
            <DialogDescription>
              {t("form.teamShareDescription", "Invite team members to edit the form together.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="label-style mb-2 block">{t("form.teamLink", "Team Link")}</Label>
              <div className="flex gap-2">
                <Input
                  value={getTeamLink()}
                  readOnly
                  className="font-mono text-xs"
                />
                <Button
                  variant="outline"
                  onClick={copyTeamLink}
                  data-testid="copy-team-link-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {t("form.copyLink", "Share this link with your colleagues.")}
              </p>
            </div>
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-slate-500">{t("form.shareViaEmail", "Or invite via email")}</span>
              </div>
            </div>
            <div>
              <Label className="label-style mb-2 block">{t("common.email", "Email Address")}</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="email"
                    placeholder="colleague@example.com"
                    value={shareEmail}
                    onChange={(e) => setShareEmail(e.target.value)}
                    className="pl-10"
                    data-testid="share-email-input"
                  />
                </div>
                <Button
                  onClick={handleShareTeam}
                  disabled={isSharingTeam}
                  className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
                  data-testid="send-team-invite-btn"
                >
                  {isSharingTeam ? "..." : t("form.invite", "Invite")}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShareDialogOpen(false)}>
              {t("common.close", "Close")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submitted Banner */}
      {isSubmitted && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <p className="text-emerald-800 font-medium">
                {t("form.formSubmitted", "Form successfully completed. An email with the PDF has been sent.")}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Version Info Banner */}
      {previousVersion && !isSubmitted && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Info className="w-5 h-5 text-blue-600" />
                <p className="text-blue-800">
                  <strong>{t("project.version", "Version")} {currentVersion}</strong> - {t("form.versionInfo", "This form has been reopened for editing.")}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowVersionCompare(true)}
                className="text-blue-700 border-blue-300 hover:bg-blue-100"
                data-testid="compare-versions-btn"
              >
                {t("form.compareVersions", "Show changes")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors Banner */}
      {validationErrors.length > 0 && !isSubmitted && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-amber-800 font-medium mb-1">
                  {t("validation.pleaseCorrect", "Please correct the following errors:")}
                </p>
                <ul className="text-sm text-amber-700 list-disc list-inside">
                  {validationErrors.map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-2xl text-slate-900 mb-2">
            {t("form.title", "Call Sequence Configuration")}
          </h1>
          <p className="text-slate-500">
            {t("form.subtitle", "Fill out the form and confirm it with your signature at the end.")}
          </p>
        </div>

        {/* Project Info */}
        <Card className="card-base mb-6" data-testid="project-info-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg">{t("form.projectData", "Project Data")}</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div>
                <Label className="label-style">{t("project.name", "Project Name")}</Label>
                <p className="mt-1 font-medium text-slate-900">{formData.project_name}</p>
              </div>
              <div>
                <Label className="label-style">{t("project.customerName", "Customer Name")}</Label>
                <Input
                  value={formData.customer_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, customer_name: e.target.value }))
                  }
                  disabled={isSubmitted}
                  className="mt-1"
                  data-testid="customer-name-input"
                />
              </div>
              <div>
                <Label className="label-style">{t("form.date", "Date")}</Label>
                <p className="mt-1 font-medium text-slate-900">{formData.date}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Participants Table */}
        <Card className="card-base mb-6" data-testid="participants-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <User className="w-5 h-5 text-slate-600" />
              {t("form.participants", "Participants")}
            </CardTitle>
            <CardDescription>
              {t("form.participantsDescription", "Add up to 10 participants that can be assigned to call sequences.")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-16 text-center">{t("form.nr", "No.")}</TableHead>
                    <TableHead className="w-48">{t("form.participantType", "Participant Type")}</TableHead>
                    <TableHead>{t("form.participantName", "Participant Name")}</TableHead>
                    <TableHead className="w-32">{t("form.countryCode", "Country Code")}</TableHead>
                    <TableHead className="w-40">{t("form.phoneNumber", "Phone Number")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.participants.map((participant, index) => (
                    <TableRow key={participant.number} className="table-row">
                      <TableCell className="text-center font-mono font-semibold text-slate-600">
                        {participant.number}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={participant.type}
                          onValueChange={(value) => updateParticipant(index, "type", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`participant-type-${index}`}>
                            <SelectValue placeholder="Auswählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            {PARTICIPANT_TYPES.map((type) => (
                              <SelectItem key={type.key} value={type.label}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={participant.name}
                          onChange={(e) => updateParticipant(index, "name", e.target.value)}
                          placeholder={t("form.enterName", "Enter name...")}
                          className="table-input"
                          disabled={isSubmitted}
                          data-testid={`participant-name-${index}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={participant.country_code}
                          onValueChange={(value) => updateParticipant(index, "country_code", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`participant-country-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COUNTRY_CODES.map((c) => (
                              <SelectItem key={c.code} value={c.code}>
                                {c.flag} {c.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={participant.phone}
                          onChange={(e) =>
                            updateParticipant(index, "phone", formatPhoneNumber(e.target.value))
                          }
                          placeholder="123456789"
                          className="table-input font-mono"
                          disabled={isSubmitted}
                          data-testid={`participant-phone-${index}`}
                          inputMode="numeric"
                          pattern="[0-9]*"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Sequences Table */}
        <Card className="card-base mb-6" data-testid="sequences-card">
          <CardHeader className="border-b border-slate-100 bg-slate-50/50">
            <CardTitle className="font-manrope text-lg flex items-center gap-2">
              <Phone className="w-5 h-5 text-slate-600" />
              {t("form.sequences", "Call Sequences")}
            </CardTitle>
            <CardDescription>
              {t("form.sequencesDescription", "Configure call sequences for each message line. Each line must end with \"Acknowledgement by\".")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-16 text-center">{t("form.messageLine", "Line")}</TableHead>
                    <TableHead className="w-40">{t("form.messageType", "Message")}</TableHead>
                    <TableHead>{t("form.assignment", "Participant Assignment (Order)")}</TableHead>
                    <TableHead className="w-24 text-center">{t("form.cycles", "Cycles")}</TableHead>
                    <TableHead className="w-24 text-center">{t("form.time", "Time (s)")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.sequences.map((sequence, seqIndex) => (
                    <TableRow key={sequence.line} className="table-row align-top">
                      <TableCell className="text-center font-mono font-semibold text-slate-600 pt-4">
                        {sequence.line}
                      </TableCell>
                      <TableCell className="pt-3">
                        <Select
                          value={sequence.message_type}
                          onValueChange={(value) => updateSequence(seqIndex, "message_type", value)}
                          disabled={isSubmitted}
                        >
                          <SelectTrigger className="table-select" data-testid={`sequence-type-${seqIndex}`}>
                            <SelectValue placeholder={t("form.select", "Select...")} />
                          </SelectTrigger>
                          <SelectContent>
                            {MESSAGE_TYPES.map((type) => (
                              <SelectItem key={type.key} value={type.label}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {sequence.message_type === t('messageTypes.testMessage') && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">
                            <Info className="w-3 h-3" />
                            {t("form.dailyAt13", "Daily at 13:00")}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="pt-3">
                        <div className="flex flex-wrap gap-2">
                          {[0, 1, 2, 3, 4].map((assignIndex) => (
                            <Select
                              key={assignIndex}
                              value={sequence.assignments?.[assignIndex]?.value || ""}
                              onValueChange={(value) =>
                                updateSequenceAssignment(seqIndex, assignIndex, value)
                              }
                              disabled={isSubmitted}
                            >
                              <SelectTrigger 
                                className="w-40 table-select text-xs" 
                                data-testid={`sequence-assign-${seqIndex}-${assignIndex}`}
                              >
                                <SelectValue placeholder={`${assignIndex + 1}. ${t("form.choice", "Choice")}`} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="keine Teilnehmer">
                                  {t("form.noParticipants", "no participants")}
                                </SelectItem>
                                {getAvailableParticipants().map((p) => (
                                  <SelectItem key={p.number} value={`${p.number} – ${p.name}`}>
                                    {p.number} – {p.name}
                                  </SelectItem>
                                ))}
                                <SelectItem value="---" disabled>
                                  — {t("form.acknowledgementBy", "Acknowledgement by")} —
                                </SelectItem>
                                <SelectItem value="einen Teilnehmer">
                                  {t("form.oneParticipant", "one participant")}
                                </SelectItem>
                                <SelectItem value="alle Teilnehmer">
                                  {t("form.allParticipants", "all participants")}
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell className="pt-3">
                        <Input
                          type="number"
                          min="1"
                          value={sequence.cycle_count || ""}
                          onChange={(e) =>
                            updateSequence(seqIndex, "cycle_count", parseInt(e.target.value) || null)
                          }
                          placeholder="12"
                          className="table-input text-center font-mono"
                          disabled={isSubmitted}
                          data-testid={`sequence-cycles-${seqIndex}`}
                        />
                      </TableCell>
                      <TableCell className="pt-3">
                        <Input
                          type="number"
                          min="1"
                          value={sequence.cycle_time || ""}
                          onChange={(e) =>
                            updateSequence(seqIndex, "cycle_time", parseInt(e.target.value) || null)
                          }
                          placeholder="30"
                          className="table-input text-center font-mono"
                          disabled={isSubmitted}
                          data-testid={`sequence-time-${seqIndex}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation & Signature */}
        {!isSubmitted && (
          <Card className="card-base mb-6" data-testid="signature-card">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-manrope text-lg">{t("form.confirmation", "Confirmation")} & {t("form.signature", "Signature")}</CardTitle>
              <CardDescription>
                {t("form.signatureDescription", "Confirm the accuracy of your information and sign the form.")}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Confirmation Checkbox */}
              <div className="flex items-start gap-3 mb-6">
                <Checkbox
                  id="confirmation"
                  checked={formData.confirmation}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, confirmation: checked }))
                  }
                  data-testid="confirmation-checkbox"
                />
                <Label htmlFor="confirmation" className="text-sm text-slate-700 cursor-pointer">
                  {t("form.confirmationText", "I confirm the accuracy of the above information")}
                </Label>
              </div>

              {/* Signature Pad */}
              <div className="mb-4">
                <Label className="label-style mb-2 block">{t("form.electronicSignature", "Electronic Signature")}</Label>
                <div className="signature-pad-wrapper">
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      className: "signature-pad-canvas",
                      "data-testid": "signature-canvas",
                    }}
                    backgroundColor="rgb(248, 250, 252)"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSignature}
                  className="mt-2 text-slate-500 hover:text-slate-700"
                  data-testid="clear-signature-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {t("form.clearSignature", "Clear signature")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Signature Display (if submitted) */}
        {isSubmitted && formData.signature && (
          <Card className="card-base mb-6">
            <CardHeader className="border-b border-slate-100 bg-slate-50/50">
              <CardTitle className="font-manrope text-lg">{t("form.signature", "Signature")}</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start gap-6">
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                  <img
                    src={formData.signature}
                    alt={t("form.signature", "Signature")}
                    className="max-w-[300px] h-auto"
                  />
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">{t("form.confirmedBy", "Confirmed by")}</p>
                  <p className="font-medium text-slate-900">{formData.customer_name}</p>
                  <p className="text-sm text-slate-500 mt-3 mb-1">{t("form.date", "Date")}</p>
                  <p className="font-medium text-slate-900">{formData.date}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        {!isSubmitted && (
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            <Button
              variant="outline"
              onClick={handleSave}
              disabled={isSaving}
              data-testid="save-btn"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900"></span>
                  {t("common.loading", "Loading...")}
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {t("form.saveIntermediate", "Save draft")}
                </>
              )}
            </Button>
            <Button
              onClick={handlePreviewPdf}
              disabled={isSubmitting || isGeneratingPdf}
              className="bg-[#1e3a5f] hover:bg-[#0d2137] text-white"
              data-testid="preview-btn"
            >
              {isGeneratingPdf ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  {t("pdf.generating", "Generating PDF...")}
                </span>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  {t("form.previewAndSubmit", "Preview & Submit")}
                </>
              )}
            </Button>
          </div>
        )}

        {/* PDF Preview Modal */}
        <PDFPreview
          isOpen={showPdfPreview}
          onClose={() => {
            setShowPdfPreview(false);
            if (pdfPreviewUrl) {
              URL.revokeObjectURL(pdfPreviewUrl);
              setPdfPreviewUrl(null);
            }
          }}
          pdfUrl={pdfPreviewUrl}
          onConfirm={handleConfirmSubmit}
          isLoading={isGeneratingPdf}
        />

        {/* Version Compare Modal */}
        <VersionCompare
          isOpen={showVersionCompare}
          onClose={() => setShowVersionCompare(false)}
          previousData={previousVersion?.form_data}
          currentData={{
            participants: formData.participants,
            sequences: formData.sequences,
            customer_name: formData.customer_name,
            version: previousVersion?.previous_version
          }}
        />
      </main>
    </div>
  );
};

export default CustomerForm;
